/**
 * **P-321** — the one `tasks.settings` store two surfaces share.
 *
 * The Tasks mail switches are now editable in two places: Tasks ▸ Preferences
 * (`TaskSettingsPanel`) and the **Tasks** section of Profile ▸ Notifications
 * (`modules/notifications/NotificationSettings.tsx`). The card's requirement is *one source, both
 * surfaces repaint* — so neither surface keeps a private copy of the six values. They subscribe
 * to the store below, keyed by the `TasksSource` they were handed, and a write made on one is the
 * value the other renders without a reload.
 *
 * It is deliberately **not** a new contract: `load` is `tasks.settings.get` and `save` is
 * `tasks.settings.set`, through the same `TasksSource` port the Tasks page already holds
 * (`trpc-source.ts`). Nothing here knows the key names, the clamping or the defaults — the
 * server does (`PREFERENCE_SCHEMAS`), which is why `save` keeps the **answer** rather than the
 * patch it sent.
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { TaskSettingsView } from './types';

/** The two members of `TasksSource` this store uses — a port, so a test needs no fixture. */
export interface TaskSettingsPort {
  settings(): Promise<TaskSettingsView>;
  saveSettings(patch: Partial<TaskSettingsView>): Promise<TaskSettingsView>;
}

export interface TaskSettingsStore {
  /** The current value, or `null` until the first read answers. */
  snapshot(): TaskSettingsView | null;
  subscribe(listener: () => void): () => void;
  /** Reads once per store; a second caller joins the first read rather than making another. */
  load(): void;
  /** Optimistic, then the server's answer — the same order `TasksPage.saveSettings` uses. */
  save(patch: Partial<TaskSettingsView>): Promise<void>;
}

function createStore(port: TaskSettingsPort): TaskSettingsStore {
  let value: TaskSettingsView | null = null;
  let reading = false;
  const listeners = new Set<() => void>();

  const publish = (next: TaskSettingsView | null): void => {
    value = next;
    for (const listener of listeners) listener();
  };

  return {
    snapshot: () => value,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load() {
      if (reading || value !== null) return;
      reading = true;
      void port
        .settings()
        .then((next) => publish(next))
        .catch(() => undefined)
        .finally(() => {
          reading = false;
        });
    },
    async save(patch) {
      if (value !== null) publish({ ...value, ...patch });
      await port
        .saveSettings(patch)
        .then((next) => publish(next))
        .catch(() => undefined);
    },
  };
}

/*
 * One store per source, held weakly: the product builds its source with `useMemo` over the tRPC
 * client, so the identity is stable for the session, and a fixture source that goes away takes
 * its store with it rather than leaking between tests.
 */
const stores = new WeakMap<TaskSettingsPort, TaskSettingsStore>();

export function taskSettingsStore(port: TaskSettingsPort): TaskSettingsStore {
  const found = stores.get(port);
  if (found) return found;
  const created = createStore(port);
  stores.set(port, created);
  return created;
}

/** The store with no port behind it — what a surface rendered without a Tasks source reads. */
const EMPTY_STORE: TaskSettingsStore = {
  snapshot: () => null,
  subscribe: () => () => undefined,
  load: () => undefined,
  save: async () => undefined,
};

export interface UseTaskSettings {
  settings: TaskSettingsView | null;
  save(patch: Partial<TaskSettingsView>): void;
}

/**
 * Subscribe to a source's settings and get a `save` that repaints every subscriber.
 *
 * `port` may be `null` — Profile ▸ Notifications renders its matrix with or without a Tasks
 * source, and the hook may not be called conditionally.
 */
export function useTaskSettings(port: TaskSettingsPort | null | undefined): UseTaskSettings {
  const store = port ? taskSettingsStore(port) : EMPTY_STORE;
  const settings = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
  /* After the subscription, never during the render: the read publishes to every subscriber. */
  useEffect(() => {
    store.load();
  }, [store]);
  return {
    settings,
    save: (patch) => {
      void store.save(patch);
    },
  };
}
